import { Routes, mapToCanActivate, mapToResolve } from '@angular/router';
import { coreRoutes } from '@next/core-lib/app';
import {
  AtLeastAdministratorRoleGuard,
  AtLeastAdvisorRoleGuard,
} from '@next/core-lib/authorization';
import { LocationGuard } from '@next/core-lib/relationships';
import { Observable } from 'rxjs';

import { AnimalGroupComponent } from './animal-group/animal-group.component';
import { AnimalGroupItemComponent } from './animal-group/components/animal-group-item/animal-group-item.component';
import { AnimalGroupOverviewComponent } from './animal-group/components/animal-group-overview/animal-group-overview.component';
import { FeedadviceApplicationsComponent } from './animal-group/components/feedadvice-applications/feedadvice-applications.component';
import { GeneralComponent } from './animal-group/components/general/general.component';
import { ObservationsComponent } from './animal-group/components/observations/observations.component';
import { AnimalGroupGuard } from './animal-group/guards/animal-group.guard';
import { DeliveryRouterComponent } from './delivery/components/delivery-router/delivery-router.component';
import { DeliveryRoutes } from './delivery/delivery.routes';
import { DeliveryOverviewResolver } from './delivery/resolvers/delivery-overview.resolver';
import { FecalScoringComponent } from './fecal-scoring/fecal-scoring.component';
import { FecalScoringRoutes } from './fecal-scoring/fecal-scoring.routes';
import { FecalScoringGuard } from './fecal-scoring/guards/fecal-scoring.guard';
import { FeedAdviceRouterComponent } from './feed-advice/components/feed-advice-router/feed-advice-router.component';
import { FeedAdviceRoutes } from './feed-advice/feed-advice.routes';
import { FeedAdvicesGuard } from './feed-advice/guards/feedadvices.guard';
import { SowsFeedAdviceRoutes } from './feed-advice/sows-feed-advice.routes';
import { ExtendedSimulationComponent } from './feed-profit/components/extended-simulation/extended-simulation.component';
import { FeedProfitItemComponent } from './feed-profit/components/feed-profit-item/feed-profit-item.component';
import { IntroComponent } from './feed-profit/components/feed-profit-item/intro/intro.component';
import { FeedProfitOverviewComponent } from './feed-profit/components/feed-profit-overview/feed-profit-overview.component';
import { QuickSimulationComponent } from './feed-profit/components/quick-simulation/quick-simulation.component';
import { FeedProfitComponent } from './feed-profit/feed-profit.component';
import { FeedProfitOverviewResolver } from './feed-profit/resolvers/feed-profit-overview.resolver';
import { ProductOverviewComponent } from './product/components/product-overview/product-overview.component';
import { CustomerProductGuard } from './product/guards/customer-product.guard';
import { ProductComponent } from './product/product.component';
import { ProductResolver } from './product/resolvers/product.resolver';
import { BarnOverviewComponent } from './settings/components/barn-overview/barn-overview.component';
import { DiscountComponent } from './settings/components/discount/discount.component';
import { DosatorSettingOverviewComponent } from './settings/components/dosator-setting-overview/dosator-setting-overview.component';
import { SettingsOverviewComponent } from './settings/components/settings-overview/settings-overview.component';

import { LeeoOverviewComponent } from './settings/components/third-parties/leeo/leeo-overview/leeo-overview.component';
import {
  NedapDashboardComponent,
  NedapInstallationOverviewComponent,
  NedapRedirectComponent,
} from './settings/components/third-parties/nedap';
import { ThirdPartiesOverviewComponent } from './settings/components/third-parties/third-parties-overview/third-parties-overview.component';
import { UomSettingComponent } from './settings/components/uom-setting/uom-setting.component';
import { SettingsBarnResolver } from './settings/resolvers/settings-barn.resolver';
import { SettingsDiscountResolver } from './settings/resolvers/settings-discount.resolver';
import { SettingsDosatorSettingResolver } from './settings/resolvers/settings-dosator.resolver';
import { SettingsUomResolver } from './settings/resolvers/settings-uom.resolver';
import { ThirdPartiesLeeOResolver } from './settings/resolvers/third-parties-lee-o.resolver';
import { ThirdPartiesNedapResolver } from './settings/resolvers/third-parties-nedap-resolver';
import { UnauthorizedComponent } from './shared/components/unauthorized/unauthorized.component';
import { TailBitingGuard } from './tail-biting/guards/tail-biting.guard';
import { TailBitingComponent } from './tail-biting/tail-biting.component';
import { TailBitingRoutes } from './tail-biting/tail-biting.routes';

const animalGroupRoutes: Routes = [
  {
    path: '',
    component: AnimalGroupOverviewComponent,
    canActivate: mapToCanActivate([LocationGuard, AnimalGroupGuard]),
    data: { name: 'animal-group-overview' },
  },
  {
    path: 'add',
    component: AnimalGroupItemComponent,
    canActivate: mapToCanActivate([LocationGuard, AnimalGroupGuard]),
    children: [
      {
        path: '',
        component: GeneralComponent,
        canActivate: mapToCanActivate([LocationGuard, AnimalGroupGuard]),
        data: { name: 'general' },
      },
    ],
  },
  {
    path: ':animalGroupId',
    component: AnimalGroupItemComponent,
    canActivate: mapToCanActivate([LocationGuard, AnimalGroupGuard]),
    children: [
      {
        path: '',
        component: GeneralComponent,
        canActivate: mapToCanActivate([LocationGuard]),
        data: { name: 'general' },
      },
      {
        path: 'feedadvice-applications',
        component: FeedadviceApplicationsComponent,
        canActivate: mapToCanActivate([LocationGuard]),
        data: { name: 'feedadviceApplications' },
      },
      {
        path: 'observations',
        component: ObservationsComponent,
        canActivate: mapToCanActivate([LocationGuard]),
        data: { name: 'observations' },
      },
    ],
  },
];

const feedProfitRoutes: Routes = [
  {
    path: '',
    component: FeedProfitOverviewComponent,
    canActivate: mapToCanActivate([LocationGuard]),
    data: { name: 'feed-profit-overview' },
    resolve: {
      data: mapToResolve<Observable<boolean>>(FeedProfitOverviewResolver),
    },
  },
  {
    path: 'create',
    component: FeedProfitItemComponent,
    canActivate: mapToCanActivate([LocationGuard]),
    children: [
      {
        path: '',
        component: IntroComponent,
        canActivate: mapToCanActivate([LocationGuard]),
        data: { name: 'feed-profit-intro' },
      },
    ],
  },
  {
    path: ':calculationId',
    component: FeedProfitItemComponent,
    canActivate: mapToCanActivate([LocationGuard]),
    children: [
      {
        path: '',
        component: IntroComponent,
        canActivate: mapToCanActivate([LocationGuard]),
        data: { name: 'feed-profit-intro' },
      },
      {
        path: 'quick-simulation',
        component: QuickSimulationComponent,
        canActivate: mapToCanActivate([LocationGuard]),
        data: { name: 'feed-profit-quick-simulation' },
      },
      {
        path: 'extended-simulation',
        component: ExtendedSimulationComponent,
        canActivate: mapToCanActivate([LocationGuard]),
        data: { name: 'feed-profit-extended-simulation' },
      },
    ],
  },
];

const settingsRoutes: Routes = [
  {
    path: 'uom-setting',
    component: UomSettingComponent,
    canActivate: mapToCanActivate([LocationGuard]),
    resolve: {
      isLoaded: mapToResolve<Observable<boolean>>(SettingsUomResolver),
    },
    data: { name: 'uom-setting' },
  },
  {
    path: 'barn',
    component: BarnOverviewComponent,
    canActivate: mapToCanActivate([LocationGuard]),
    resolve: {
      isLoaded: mapToResolve<Observable<boolean>>(SettingsBarnResolver),
    },
    data: { name: 'barn-overview' },
  },
  {
    path: 'discount',
    component: DiscountComponent,
    canActivate: mapToCanActivate([LocationGuard]),
    resolve: {
      isLoaded: mapToResolve<Observable<boolean>>(SettingsDiscountResolver),
    },
    data: { name: 'discount' },
  },
  {
    path: 'dosator-setting',
    component: DosatorSettingOverviewComponent,
    canActivate: mapToCanActivate([LocationGuard]),
    resolve: {
      isLoaded: mapToResolve<Observable<boolean>>(SettingsDosatorSettingResolver),
    },
    data: { name: 'dosator-setting-overview' },
  },
];

const thirdPartiesRoutes: Routes = [
  {
    path: 'nedap',
    redirectTo: 'nedap/installation-overview',
    pathMatch: 'full',
  },
  {
    path: 'nedap/installation-overview',
    component: NedapInstallationOverviewComponent,
    canActivate: mapToCanActivate([LocationGuard]),
    data: { name: 'nedap-installation-overview' },
  },
  {
    path: 'leeo',
    component: LeeoOverviewComponent,
    canActivate: mapToCanActivate([LocationGuard]),
    resolve: {
      isLoaded: mapToResolve<Observable<boolean>>(ThirdPartiesLeeOResolver),
    },
    data: { name: 'leeo-overview' },
  },
];

export const appRoutes: Routes = [
  { path: '', redirectTo: 'feed-advice', pathMatch: 'full' },
  {
    path: 'animal-group',
    component: AnimalGroupComponent,
    children: animalGroupRoutes,
    canActivate: mapToCanActivate([LocationGuard]),
  },
  {
    path: 'delivery',
    component: DeliveryRouterComponent,
    children: DeliveryRoutes(),
    resolve: {
      data: DeliveryOverviewResolver,
    },
  },
  {
    path: 'fecal-scoring',
    component: FecalScoringComponent,
    children: FecalScoringRoutes(),
    canActivate: mapToCanActivate([FecalScoringGuard]),
  },
  {
    path: 'feed-advice',
    component: FeedAdviceRouterComponent,
    children: FeedAdviceRoutes(false),
    canActivate: mapToCanActivate([LocationGuard, FeedAdvicesGuard]),
  },
  {
    path: 'sows-feed-advice',
    component: FeedAdviceRouterComponent,
    children: SowsFeedAdviceRoutes(false),
  },
  {
    path: 'reference-feed-advice',
    component: FeedAdviceRouterComponent,
    children: FeedAdviceRoutes(true),
    canActivate: mapToCanActivate([FeedAdvicesGuard]),
  },
  {
    path: 'reference-sows-feed-advice',
    component: FeedAdviceRouterComponent,
    children: SowsFeedAdviceRoutes(true),
  },
  {
    path: 'feed-profit',
    component: FeedProfitComponent,
    children: feedProfitRoutes,
    canActivate: mapToCanActivate([LocationGuard]),
  },
  {
    path: 'product',
    component: ProductComponent,
    children: [
      {
        path: '',
        component: ProductOverviewComponent,
        canActivate: mapToCanActivate([LocationGuard, AtLeastAdministratorRoleGuard]),
        resolve: {
          data: mapToResolve<Observable<boolean>>(ProductResolver),
        },
        data: { name: 'product-overview' },
        runGuardsAndResolvers: 'always',
      },
    ],
    canActivate: mapToCanActivate([LocationGuard, AtLeastAdministratorRoleGuard]),
  },
  {
    path: 'customer-product',
    component: ProductComponent,
    children: [
      {
        path: '',
        component: ProductOverviewComponent,
        canActivate: mapToCanActivate([
          LocationGuard,
          CustomerProductGuard,
          AtLeastAdvisorRoleGuard,
        ]),
        data: { name: 'customer-product-overview' },
        resolve: {
          data: mapToResolve<Observable<boolean>>(ProductResolver),
        },
        runGuardsAndResolvers: 'always',
      },
    ],
    canActivate: mapToCanActivate([LocationGuard, AtLeastAdvisorRoleGuard]),
  },
  {
    path: 'settings',
    redirectTo: 'settings/uom-setting',
    pathMatch: 'full',
  },
  {
    path: 'settings',
    component: SettingsOverviewComponent,
    children: settingsRoutes,
    canActivate: mapToCanActivate([LocationGuard]),
  },
  {
    path: 'third-parties',
    redirectTo: 'third-parties/nedap/installation-overview',
    pathMatch: 'full',
  },
  {
    path: 'third-parties/nedap/response',
    component: NedapRedirectComponent,
  },
  {
    path: 'nedap-dashboard',
    component: NedapDashboardComponent,
    canActivate: mapToCanActivate([LocationGuard]),
  },
  {
    path: 'third-parties',
    component: ThirdPartiesOverviewComponent,
    resolve: {
      isLoaded: mapToResolve<Observable<boolean>>(ThirdPartiesNedapResolver),
    },
    children: thirdPartiesRoutes,
  },
  {
    path: 'tail-biting',
    component: TailBitingComponent,
    children: TailBitingRoutes(),
    canActivate: mapToCanActivate([TailBitingGuard]),
  },
  {
    path: 'unauthorized',
    component: UnauthorizedComponent,
  },

  // Core lib routes (error pages, not found, etc.)
  ...coreRoutes,
];
